import { useState, useEffect, useRef } from 'react';

const K = {
  b1: '#050e28', b2: '#0f38b0', b3: '#2462f0',
  f1: '#1c4ed0', f2: '#0b2260',
  acc: '#4e90ff', glo: '#7ab8ff',
  dark: '#02080e', green: '#3fd47c',
};

function useTick(on = true) {
  const [t, set] = useState(0);
  useEffect(() => {
    if (!on) return;
    let alive = true, raf;
    const s = performance.now();
    const step = n => { if (!alive) return; set((n - s) / 1e3); raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => { alive = false; cancelAnimationFrame(raf); };
  }, [on]);
  return t;
}

// Animated wavy-bottom ghost body path — 3 staggered sine bumps
function ghostBody(t, state, anim) {
  const sleeping = state === 'sleeping';
  const excited  = state === 'excited';
  const working  = state === 'thinking';
  // Excited was 5.5/7 — fast enough and wide enough that the middle leg's
  // swing read as an inappropriate gesture rather than excitement. Toned
  // down to still feel energetic without that.
  const spd = sleeping ? 0.4 : excited ? 3.2 : working ? 0.8 : 1.5;
  const amp = sleeping ? 1.5 : excited ? 4   : working ? 2   : 4;
  const w1 = (anim && !sleeping) ? Math.sin(t * spd)       * amp : 0;
  const w2 = (anim && !sleeping) ? Math.sin(t * spd + 1.1) * amp : 0;
  const w3 = (anim && !sleeping) ? Math.sin(t * spd + 2.2) * amp : 0;
  const rb = (132 + w3).toFixed(1), rv = (108 + w1).toFixed(1);
  const cb = (134 + w1).toFixed(1), cv = (108 + w3).toFixed(1);
  const lb = (132 + w2).toFixed(1);
  return (
    `M 60 16 C 97 16 112 42 112 72 C 112 95 110 108 106 112 ` +
    `Q 94 ${rb} 80 ${rv} Q 60 ${cb} 40 ${cv} Q 26 ${lb} 14 112 ` +
    `C 10 108 8 95 8 72 C 8 42 23 16 60 16 Z`
  );
}

// Props:
//   size     — pixel width (height auto-scales, aspect 150/120)
//   state    — 'idle' | 'thinking' | 'happy' | 'surprised' | 'focused' | 'sleeping' | 'excited' | 'smug'
//   animate  — boolean (default true)
export function JAIMascot({ size = 240, state = 'idle', animate = true }) {
  const t = useTick(animate);
  const uid = useRef('g' + Math.random().toString(36).slice(2, 6)).current;

  const working   = state === 'thinking';
  const happy     = state === 'happy';
  const surprised = state === 'surprised';
  const focused   = state === 'focused';
  const sleeping  = state === 'sleeping';
  const excited   = state === 'excited';
  const smug      = state === 'smug';
  const halfLid   = state === 'idle' || smug;

  // Float + shake
  const floatSpd = sleeping ? 0.45 : happy ? 4.4 : excited ? 0 : 1.15;
  const floatAmp = sleeping ? 2 : happy ? 2.5 : 3;
  const bob = animate ? Math.sin(t * floatSpd) * floatAmp : 0;
  const shk = (excited && animate)
    ? { x: Math.sin(t * 29) * 2.8, y: Math.sin(t * 24) * 1.8 }
    : { x: 0, y: 0 };

  // Eyes
  const blink  = animate && !working && !surprised && !sleeping && !excited && (t % 5) < 0.13;
  const ex     = animate ? Math.sin(t * 0.6)  * (working ? 0.8 : sleeping || focused ? 0 : 2.6) : 0;
  const ey     = animate ? Math.sin(t * 0.44) * (working ? 0.4 : sleeping || focused ? 0 : 1.7) : 0;
  const eyeRx  = (surprised || excited) ? 13   : 11;
  const eyeRy  = (surprised || excited) ? 13.5 : focused ? 4 : smug ? 8 : halfLid ? 9 : 11.5;
  const pupR   = (surprised || excited) ? 9    : focused ? 3 : 6.5;

  // Aura
  const aOp = working ? 0.18 + Math.sin(t * 3.8) * 0.07
            : excited ? 0.24 + Math.sin(t * 9) * 0.1
            : 0.06 + Math.sin(t * 1.2) * 0.02;

  // Orbit particles (thinking)
  const orbs = working ? [0,1,2,3,4].map(i => {
    const a = ((t * 52 + i * 72) % 360) * Math.PI / 180;
    const r = 58 + Math.sin(t * 2.2 + i * 1.3) * 5;
    return {
      x: 60 + Math.cos(a) * r, y: 72 + Math.sin(a) * r * 0.62,
      r: 1.4 + Math.sin(t * 4 + i) * 0.45,
      op: 0.28 + Math.sin(t * 3.2 + i) * 0.14,
    };
  }) : [];

  // Radial sparks (excited)
  const sparks = excited ? [0,1,2,3,4,5,6,7].map(i => {
    const a = ((t * 220 + i * 45) % 360) * Math.PI / 180;
    const r1 = 52 + Math.sin(t * 6 + i) * 6;
    const r2 = r1 + 8 + Math.sin(t * 8 + i) * 4;
    return {
      x1: 60 + Math.cos(a) * r1, y1: 72 + Math.sin(a) * r1 * 0.65,
      x2: 60 + Math.cos(a) * r2, y2: 72 + Math.sin(a) * r2 * 0.65,
      op: 0.4 + Math.sin(t * 7 + i) * 0.35,
    };
  }) : [];

  // ZZZ (sleeping)
  const zzz = sleeping ? [0,1,2].map(i => {
    const phase = ((t * 0.7 + i * 1.6) % 4.8);
    return {
      x: 68 + i * 9, y: 14 - phase * 13,
      op: Math.min(1, phase < 2.4 ? phase * 0.4 : (4.8 - phase) * 0.6),
      sz: 9 + i * 2,
    };
  }) : [];

  const body = ghostBody(t, state, animate);

  // Brows
  const browsEl = (() => {
    if (happy || sleeping || blink) return null;
    const configs = {
      thinking : ['M 34 52 L 52 54', 'M 68 54 L 86 52'],
      surprised: ['M 34 48 L 52 46', 'M 68 46 L 86 48'],
      focused  : ['M 34 53 L 52 56', 'M 68 56 L 86 53'],
      smug     : ['M 34 53 L 52 52', 'M 68 50 L 86 54'],
      excited  : ['M 34 50 L 52 48', 'M 68 48 L 86 50'],
      idle     : ['M 34 53 L 52 52', 'M 68 52 L 86 53'],
    };
    const [l, r] = configs[state] || configs.idle;
    return (
      <>
        <path d={l} stroke={K.glo} strokeWidth="2.6" strokeLinecap="round" fill="none" opacity="0.72"/>
        <path d={r} stroke={K.glo} strokeWidth="2.6" strokeLinecap="round" fill="none" opacity="0.72"/>
      </>
    );
  })();

  // Mouth
  const mouthEl = (() => {
    if (sleeping)           return <line x1="53" y1="82" x2="67" y2="82" stroke={K.glo} strokeWidth="2.2" strokeLinecap="round" opacity="0.45"/>;
    if (happy)              return <path d="M 40 84 Q 60 101 80 84" stroke="white" strokeWidth="3.2" fill="none" strokeLinecap="round"/>;
    if (surprised)          return <ellipse cx="60" cy="88" rx="8" ry="10" fill="none" stroke="white" strokeWidth="2.8"/>;
    if (excited)            return <path d="M 40 83 Q 60 104 80 83" stroke="white" strokeWidth="3.5" fill="none" strokeLinecap="round"/>;
    if (working || focused) return <line x1="51" y1="86" x2="69" y2="86" stroke="white" strokeWidth="3" strokeLinecap="round" opacity="0.88"/>;
    if (smug)               return <path d="M 50 86 Q 64 86 72 81" stroke="white" strokeWidth="2.8" fill="none" strokeLinecap="round"/>;
    /* idle */              return <path d="M 51 86 Q 60 94 69 86" stroke="white" strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.88"/>;
  })();

  return (
    <svg
      width={size}
      height={size * 150 / 120}
      viewBox="0 0 120 150"
      style={{ overflow: 'visible', display: 'block' }}
    >
      <defs>
        <radialGradient id={uid+'bd'} cx="0.4" cy="0.26" r="0.72">
          <stop offset="0%"   stopColor="#2a68f2"/>
          <stop offset="44%"  stopColor="#1038b4"/>
          <stop offset="100%" stopColor="#050e28"/>
        </radialGradient>
        <radialGradient id={uid+'ir'} cx="0.3" cy="0.28" r="0.74">
          <stop offset="0%"   stopColor={working ? '#5aabff' : sleeping ? '#506090' : '#141428'}/>
          <stop offset="100%" stopColor={working ? '#1a4ed6' : sleeping ? '#282e48' : '#020610'}/>
        </radialGradient>
        <radialGradient id={uid+'aura'} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0%"   stopColor="#3a7cff" stopOpacity={aOp * 5}/>
          <stop offset="100%" stopColor="#3a7cff" stopOpacity="0"/>
        </radialGradient>
      </defs>

      {/* Ground shadow */}
      <ellipse
        cx="60" cy="148"
        rx={22 - Math.abs(bob) * 0.4}
        ry="2.5"
        fill={K.dark}
        opacity={0.2 - Math.abs(bob) * 0.016}
      />

      <g transform={`translate(${shk.x} ${bob + shk.y})`}>

        {/* Aura */}
        <ellipse cx="60" cy="74" rx="72" ry="78" fill={`url(#${uid}aura)`}/>

        {/* Orbit particles — thinking */}
        {orbs.map((o, i) => <circle key={i} cx={o.x} cy={o.y} r={o.r} fill={K.acc} opacity={o.op}/>)}

        {/* Radial sparks — excited */}
        {sparks.map((s, i) => (
          <line key={i} x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
            stroke={K.acc} strokeWidth="1.8" strokeLinecap="round" opacity={s.op}/>
        ))}

        {/* Ghost body */}
        <path d={body} fill={`url(#${uid}bd)`}/>

        {/* Specular gloss */}
        <ellipse cx="41" cy="43" rx="14" ry="7.5" fill="white" opacity="0.12" transform="rotate(-26 41 43)"/>
        <ellipse cx="39" cy="42" rx="7.5" ry="4"  fill="white" opacity="0.08" transform="rotate(-26 39 42)"/>

        {/* ZZZ — sleeping */}
        {zzz.map((z, i) => (
          <text key={i} x={z.x} y={z.y}
            fontFamily="'Inter Tight', Inter, system-ui, sans-serif"
            fontSize={z.sz} fontWeight="800"
            fill={K.glo} opacity={z.op * 0.75} textAnchor="middle">z</text>
        ))}

        {/* Brows */}
        {browsEl}

        {/* Eyes */}
        {sleeping ? (
          <>
            <path d="M 33 65 Q 44 57 55 65" stroke={K.glo} strokeWidth="3.2" fill="none" strokeLinecap="round" opacity="0.65"/>
            <path d="M 65 65 Q 76 57 87 65" stroke={K.glo} strokeWidth="3.2" fill="none" strokeLinecap="round" opacity="0.65"/>
          </>
        ) : blink ? (
          <>
            <path d="M 33 63 Q 44 55 55 63" stroke="white" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
            <path d="M 65 63 Q 76 55 87 63" stroke="white" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
          </>
        ) : happy ? (
          <>
            <path d="M 33 66 Q 44 54 55 66" stroke="white" strokeWidth="3.8" fill="none" strokeLinecap="round"/>
            <path d="M 65 66 Q 76 54 87 66" stroke="white" strokeWidth="3.8" fill="none" strokeLinecap="round"/>
          </>
        ) : (
          <>
            {/* Left eye */}
            {halfLid
              ? <path d={`M 33 63 L 55 63 A 11 ${eyeRy} 0 0 1 33 63 Z`} fill="white"/>
              : <ellipse cx="44" cy="63" rx={eyeRx} ry={eyeRy} fill="white"/>
            }
            <ellipse cx={44+ex} cy={halfLid ? 65+ey : 63+ey} rx={pupR} ry={pupR} fill={`url(#${uid}ir)`}/>
            <circle cx={44+ex+3}  cy={(halfLid?65:63)+ey-3}   r="2.3" fill="white" opacity="0.95"/>
            <circle cx={44+ex-2}  cy={(halfLid?65:63)+ey+2.5} r="1"   fill="white" opacity="0.35"/>
            {working && <>
              <circle cx={44+ex} cy={63+ey} r={pupR+1.2} fill="none" stroke={K.glo} strokeWidth="0.9" opacity="0.6"/>
              <rect x="32.5" y={57+(Math.sin(t*6)+1)*6} width="23" height="1.3" fill={K.acc} opacity="0.55"/>
            </>}

            {/* Right eye */}
            {halfLid
              ? <path d={`M 65 63 L 87 63 A 11 ${eyeRy} 0 0 1 65 63 Z`} fill="white"/>
              : <ellipse cx="76" cy="63" rx={eyeRx} ry={eyeRy} fill="white"/>
            }
            <ellipse cx={76+ex} cy={halfLid ? 65+ey : 63+ey} rx={pupR} ry={pupR} fill={`url(#${uid}ir)`}/>
            <circle cx={76+ex+3}  cy={(halfLid?65:63)+ey-3}   r="2.3" fill="white" opacity="0.95"/>
            <circle cx={76+ex-2}  cy={(halfLid?65:63)+ey+2.5} r="1"   fill="white" opacity="0.35"/>
            {working && <>
              <circle cx={76+ex} cy={63+ey} r={pupR+1.2} fill="none" stroke={K.glo} strokeWidth="0.9" opacity="0.6"/>
              <rect x="64.5" y={57+(Math.sin(t*6+1.3)+1)*6} width="23" height="1.3" fill={K.acc} opacity="0.55"/>
            </>}
          </>
        )}

        {/* Mouth */}
        {mouthEl}

        {/* Thinking dots */}
        {working && [0,1,2].map(i => (
          <circle key={i} cx={54+i*6} cy="115" r="2.9" fill={K.acc}
            opacity={0.35 + 0.6*Math.abs(Math.sin(t*4.5 + i*1.15))}/>
        ))}

      </g>
    </svg>
  );
}

export default JAIMascot;
